import React, { useEffect, useState } from "react";
import { GetAllInterviewScheduleCandidates } from "../Services/InterviewScheduledService";

import {
  SelectedCandidate,
  RejectedCandidates,
  HoldCandidates,
  BackOutCandidate,
  GetCandidateInterviewDetails,
  viewResumeUrl,
  StartBGV,
  ScheduleNextInterview,
  SystemRemark,
  GetInterviewFeedbackByUID,
} from "../../CandidateServices/CandidateService";

import { MoreVertical, FileText, Funnel } from "lucide-react";
import Pagination from "../../../CommonModals/Pagination";
import ViewResumeModal from "../../../Jobs/SendCandidates/Modals/ViewResumeModal";
import NormalAlert from "../../../../Alerts/NormalAlert";
import ScheduleInterviewModal from "../Modal/ScheduleInterviewModal";
import InterviewFeedbackModal from "../Modal/InterviewFeedbackModal";
import CandidateDetailsModal from "../../NewApplications/Modal/CandidateDetailsModal";
import ColumnFilterModal from "../../../CommonModals/ColumnFilterModal";
import SystemRemarkModal from "../../NewApplications/Modal/SystemRemarkModal";

const InterviewScheduledApplication = () => {
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [alert, setAlert] = useState(null);

  const [showResumeModal, setShowResumeModal] = useState(false);
  const [resumeUrl, setResumeUrl] = useState("");

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [candidateDetails, setCandidateDetails] = useState(null);

  const [modalType, setModalType] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [remark, setRemark] = useState("");

  const [showSystemRemark, setShowSystemRemark] = useState(false);
  const [systemRemarkData, setSystemRemarkData] = useState(null);
  const [showSystemRemarkModal, setShowSystemRemarkModal] = useState(false);
  const [systemRemarkLoading, setSystemRemarkLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [interviewDetails, setInterviewDetails] = useState([]);

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [interviewDateTime, setInterviewDateTime] = useState("");

  const [actionLoading, setActionLoading] = useState(false);

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackData, setFeedbackData] = useState(null);

  const [activeFilter, setActiveFilter] = useState(null);
  const [filterSearch, setFilterSearch] = useState("");

  const [filters, setFilters] = useState({
    jobTitel: [],
    submitByType: [],
    functionName: [],
    department: [],
    grade: [],
  });

  const filterOptions = {
    jobTitel: [...new Set(candidates.map((c) => c.jobTitel))],
    submitByType: [...new Set(candidates.map((c) => c.submitByType))],
    functionName: [...new Set(candidates.map((c) => c.functionName))],
    department: [...new Set(candidates.map((c) => c.department))],
    grade: [...new Set(candidates.map((c) => c.grade))],
  };

  const filteredCandidates = candidates.filter((candidate) => {
    const matchesJobTitle =
      (filters.jobTitel || []).length === 0 ||
      (filters.jobTitel || []).includes(candidate.jobTitel);

    const matchesSubmitType =
      (filters.submitByType || []).length === 0 ||
      (filters.submitByType || []).includes(candidate.submitByType);

    const matchesFunction =
      (filters.functionName || []).length === 0 ||
      (filters.functionName || []).includes(candidate.functionName);

    const matchesDepartment =
      (filters.department || []).length === 0 ||
      (filters.department || []).includes(candidate.department);

    const matchesGrade =
      (filters.grade || []).length === 0 ||
      (filters.grade || []).includes(candidate.grade);

    const matchesSearch = [
      candidate.jobTitel,
      candidate.submitByType,
      candidate.functionName,
      candidate.department,
      candidate.grade,
      candidate.name,
      candidate.email,
    ].some((field) => field?.toLowerCase().includes(searchTerm.toLowerCase()));

    return (
      matchesJobTitle &&
      matchesSubmitType &&
      matchesFunction &&
      matchesDepartment &&
      matchesGrade &&
      matchesSearch
    );
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  // Fetch Data
  const fetchData = async () => {
    try {
      const res = await GetAllInterviewScheduleCandidates();
      const data = res?.data;

      if (!data) throw new Error();

      setCandidates(data.interviewScheduledCandidates || []);

      if (data.status === "Fail" && data.message !== "No candidate found.") {
        setAlert({
          type: "fail",
          message: data.message || "Failed to load candidates",
        });
      }
    } catch (err) {
      const backendMessage = err?.response?.data?.message;

      if (backendMessage !== "No candidate found.") {
        setAlert({
          type: "fail",
          message: backendMessage || "Failed to load candidates",
        });
      }

      setCandidates([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";

    try {
      return dateStr.split("T")[0];
    } catch {
      return "";
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchData();
      setLoading(false);
    };

    loadData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".action-menu")) {
        setOpenMenu(null);
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // Pagination
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentCandidates = filteredCandidates.slice(indexOfFirst, indexOfLast);

  // View Details
  const handleNameClick = async (uid) => {
    try {
      const res = await GetCandidateInterviewDetails(uid);
      const data = res?.data || res;

      if (data?.interviewDetails?.status === "Success") {
        setCandidateDetails({
          candidateDetails: data.interviewDetails?.candidateDetails,
          jobDetails: data.interviewDetails?.jobDetails,
        });

        setInterviewDetails(data.interviewDetails?.jobInterviewers || []);
        setFeedbackData(data.feedbackDetails);

        setSelectedCandidate(data.interviewDetails?.candidateDetails);

        setShowDetailsModal(true);
      } else {
        setAlert({ type: "fail", message: "Failed to load details" });
      }
    } catch {
      setAlert({ type: "fail", message: "Failed to load details" });
    }
  };

  const handleViewFeedback = async (interviewer) => {
    try {
      const res = await GetInterviewFeedbackByUID(
        selectedCandidate.submitedCandidateUID,
        interviewer.interviewUID,
      );

      if (res?.status === "Success" && res?.interviews?.length > 0) {
        setFeedbackData(res.interviews[0]);
      } else {
        setFeedbackData(null);
      }

      setShowDetailsModal(false);

      setTimeout(() => {
        setShowFeedbackModal(true);
      }, 100);
    } catch {
      setFeedbackData(null);
      setShowDetailsModal(false);
      setShowFeedbackModal(true);
    }
  };

  const handleViewSystemRemark = async () => {
    try {
      setSystemRemarkLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const response = await SystemRemark(
        selectedCandidate.submitedCandidateUID,
      );

      if (response?.data?.status === "Success") {
        setSystemRemarkData(response.data.data);
        setShowSystemRemarkModal(true);
      } else {
        setAlert({
          type: "fail",
          message: "Failed to fetch system remark",
        });
      }
    } catch (error) {
      setAlert({
        type: "fail",
        message: error?.response?.data?.message || "Something went wrong",
      });
    } finally {
      setSystemRemarkLoading(false);
    }
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return null;
    const date = new Date(dateTime);
    const yyyy = date.getFullYear();
    const MM = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const HH = String(date.getHours()).padStart(2, "0");
    const mm = String(date.getMinutes()).padStart(2, "0");
    const ss = String(date.getSeconds()).padStart(2, "0");
    const fff = String(date.getMilliseconds()).padStart(3, "0");

    return `${yyyy}-${MM}-${dd} ${HH}:${mm}:${ss}.${fff}`;
  };

  const handleScheduleClick = (interviewer) => {
    setSelectedInterview(interviewer);
    setShowDetailsModal(false);
    setShowScheduleModal(true);
  };

  const handleSchedule = async () => {
    if (!selectedInterview?.interviewUID) {
      setAlert({ type: "error", message: "InterviewUID is missing!" });
      return;
    }

    const formattedDate = formatDateTime(interviewDateTime);

    try {
      setLoading(true);
      const response = await ScheduleNextInterview(
        selectedInterview.interviewUID,
        formattedDate,
      );

      if (response.data.status === "Success") {
        setAlert({ type: "success", message: response.data.message });
        setShowScheduleModal(false);
      } else {
        setAlert({ type: "error", message: response.data.message });
      }
    } catch (error) {
      setAlert({
        type: "error",
        message: "Something went wrong. Please try again.",
      });
    }
  };

  // Resume
  const handleResumeClick = async (candidate) => {
    if (!candidate?.resumePath) {
      setAlert({ type: "fail", message: "Resume not available" });
      return;
    }

    try {
      const fileName = candidate.resumePath.split("/").pop();
      const url = await viewResumeUrl(fileName);

      setResumeUrl(url);
      setShowResumeModal(true);
    } catch {
      setAlert({ type: "fail", message: "Unable to load resume" });
    }
  };

  // Candidate Actions
  const handleActionSubmit = async () => {
    try {
      setActionLoading(true);

      let res;

      if (modalType === "Select") {
        res = await SelectedCandidate(
          selectedCandidate.submitedCandidateUID,
          5,
          remark,
        );
      }

      if (modalType === "Rejected") {
        res = await RejectedCandidates(
          selectedCandidate.submitedCandidateUID,
          7,
          remark,
        );
      }

      if (modalType === "Hold") {
        res = await HoldCandidates(
          selectedCandidate.submitedCandidateUID,
          8,
          remark,
        );
      }

      if (modalType === "Backout") {
        res = await BackOutCandidate(
          selectedCandidate.submitedCandidateUID,
          10,
          remark,
        );
      }

      if (modalType === "BGV") {
        res = await StartBGV(
          selectedCandidate.submitedCandidateUID,
          11,
          remark,
        );
      }

      if (res?.data) {
        setAlert({
          type:
            res.data.status?.toLowerCase() === "success" ? "success" : "fail",
          message: res.data.message,
        });

        setShowActionModal(false);
        await fetchData();
      }
    } catch {
      setAlert({ type: "fail", message: "Action failed" });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <div className="w-full py-8 mt-12">
        <div className="max-w-[1240px] mx-auto">
          <div className="bg-white rounded-2xl px-6 py-6 shadow-sm">
            {/* HEADER */}
            <div className="bg-master-header text-white rounded-xl px-3 py-2 mb-6">
              <h3 className="font-bold text-lg">
                Interview Scheduled Candidates
              </h3>
            </div>
            {/* SEARCH */}
            <div className="mb-6">
              <input
                type="text"
                placeholder="Search by name, title etc"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded-full px-5 py-2 w-full max-w-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* TABLE */}
            <div className="border rounded-lg overflow-visible">
              <table className="w-full">
                <thead className="bg-[#f5f8ff] text-sm uppercase">
                  <tr>
                    <th className="py-3 text-left w-[5%]">#</th>

                    <th className="py-3 text-left w-[20%]">Candidate Info</th>

                    <th className="py-3 text-left w-[15%]">
                      <div className="flex items-center gap-2">
                        Submitted By
                        <Funnel
                          size={14}
                          onClick={() => setActiveFilter("submitByType")}
                          className="cursor-pointer text-primary"
                        />
                      </div>
                    </th>

                    <th className="py-3 text-left w-[20%]">
                      <div className="flex items-center gap-2">
                        Role Info
                        <Funnel
                          size={14}
                          onClick={() => setActiveFilter("jobTitel")}
                          className="cursor-pointer text-primary"
                        />
                      </div>
                    </th>

                    <th className="py-3 text-left w-[15%]">
                      <div className="flex items-center gap-2">
                        Function
                        <Funnel
                          size={14}
                          onClick={() => setActiveFilter("functionName")}
                          className="cursor-pointer text-primary"
                        />
                      </div>
                    </th>
                    <th className="py-3 text-left w-[15%]">
                      <div className="flex items-center gap-2">
                        Department
                        <Funnel
                          size={14}
                          onClick={() => setActiveFilter("department")}
                          className="cursor-pointer text-primary"
                        />
                      </div>
                    </th>
                    <th className="py-3 text-left w-[20%]">
                      <div className="flex items-center gap-2">
                        Grade
                        <Funnel
                          size={14}
                          onClick={() => setActiveFilter("grade")}
                          className="cursor-pointer text-primary"
                        />
                      </div>
                    </th>

                    <th className="py-3 text-center w-[10%]">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {currentCandidates.length ? (
                    currentCandidates.map((candidate, idx) => (
                      <tr key={idx} className="border-t hover:bg-gray-50">
                        <td className="py-6">{indexOfFirst + idx + 1}</td>
                        {/* <td>{candidate.roleCode || "--"}</td> */}
                        <td className="cursor-pointer text-left">
                          <div className="flex flex-col items-start">
                            <span
                              className="text-blue-500 font-semibold"
                              onClick={() =>
                                handleNameClick(candidate.submitedCandidateUID)
                              }
                            >
                              {candidate.name || "-"}
                            </span>

                            <span className="text-gray-600 text-sm">
                              {candidate.mobile || "-"}
                            </span>

                            <span className="text-gray-500 text-xs">
                              {candidate.email || "-"}
                            </span>
                          </div>
                        </td>

                        <td className="text-left">
                          <div className="flex flex-col items-start">
                            <span className="text-sm font-semibold">
                              {candidate.submitedBy}
                            </span>

                            <span className="text-gray-500 text-sm">
                              {candidate.submitByType}
                            </span>

                            <span className="text-gray-500 text-sm">
                              {formatDate(candidate.submitDate)}
                            </span>
                          </div>
                        </td>

                        <td className="text-left">
                          <div className="flex flex-col items-start">
                            <span className="text-sm font-semibold">
                              {candidate.roleCode || "-"}
                            </span>

                            <span className="text-gray-500 text-sm">
                              {candidate.jobTitel || "-"}
                            </span>
                          </div>
                        </td>

                        {/* <td>{candidate.jobTitel}</td> */}
                        <td>{candidate.functionName}</td>
                        <td>{candidate.department}</td>
                        <td>{candidate.grade}</td>

                        <td className="text-center">
                          <div className="flex justify-center items-center gap-3 relative">
                            {/* Resume */}
                            <button
                              onClick={() => handleResumeClick(candidate)}
                              className="p-1 hover:bg-gray-100 rounded-full"
                            >
                              <FileText size={18} className="text-blue-500" />
                            </button>

                            {/* Dropdown */}
                            <div className="relative inline-block action-menu">
                              <button
                                onClick={() =>
                                  setOpenMenu(
                                    openMenu === candidate.submitedCandidateUID
                                      ? null
                                      : candidate.submitedCandidateUID,
                                  )
                                }
                                className="p-1 hover:bg-gray-100 rounded-full"
                              >
                                <MoreVertical size={18} />
                              </button>

                              {openMenu === candidate.submitedCandidateUID && (
                                <div className="absolute right-0 mt-2 w-44 bg-white border rounded-lg shadow-lg z-50">
                                  <button
                                    onClick={() =>
                                      handleNameClick(
                                        candidate.submitedCandidateUID,
                                      )
                                    }
                                    className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                  >
                                    Reschedule Interview
                                  </button>

                                  <button
                                    onClick={async () => {
                                      setOpenMenu(null);

                                      const res =
                                        await GetCandidateInterviewDetails(
                                          candidate.submitedCandidateUID,
                                        );

                                      const data = res?.data || res;

                                      if (
                                        data?.interviewDetails?.status ===
                                        "Success"
                                      ) {
                                        setCandidateDetails({
                                          candidateDetails:
                                            data.interviewDetails
                                              ?.candidateDetails,
                                          jobDetails:
                                            data.interviewDetails?.jobDetails,
                                        });

                                        setInterviewDetails(
                                          data.interviewDetails
                                            ?.jobInterviewers || [],
                                        );

                                        setSelectedCandidate(
                                          data.interviewDetails
                                            ?.candidateDetails,
                                        );
                                        setModalType("Select");
                                        setShowDetailsModal(true);
                                      }
                                    }}
                                    className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                  >
                                    Select
                                  </button>

                                  <button
                                    onClick={async () => {
                                      setOpenMenu(null);

                                      const res =
                                        await GetCandidateInterviewDetails(
                                          candidate.submitedCandidateUID,
                                        );

                                      const data = res?.data || res;

                                      if (
                                        data?.interviewDetails?.status ===
                                        "Success"
                                      ) {
                                        setCandidateDetails({
                                          candidateDetails:
                                            data.interviewDetails
                                              ?.candidateDetails,
                                          jobDetails:
                                            data.interviewDetails?.jobDetails,
                                        });

                                        setInterviewDetails(
                                          data.interviewDetails
                                            ?.jobInterviewers || [],
                                        );

                                        setSelectedCandidate(
                                          data.interviewDetails
                                            ?.candidateDetails,
                                        );
                                        setModalType("Rejected");
                                        setShowDetailsModal(true);
                                      }
                                    }}
                                    className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                  >
                                    Reject
                                  </button>

                                  <button
                                    onClick={async () => {
                                      setOpenMenu(null);

                                      const res =
                                        await GetCandidateInterviewDetails(
                                          candidate.submitedCandidateUID,
                                        );

                                      const data = res?.data || res;

                                      if (
                                        data?.interviewDetails?.status ===
                                        "Success"
                                      ) {
                                        setCandidateDetails({
                                          candidateDetails:
                                            data.interviewDetails
                                              ?.candidateDetails,
                                          jobDetails:
                                            data.interviewDetails?.jobDetails,
                                        });

                                        setInterviewDetails(
                                          data.interviewDetails
                                            ?.jobInterviewers || [],
                                        );

                                        setSelectedCandidate(
                                          data.interviewDetails
                                            ?.candidateDetails,
                                        );
                                        setModalType("Hold");
                                        setShowDetailsModal(true);
                                      }
                                    }}
                                    className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                  >
                                    Hold
                                  </button>

                                  <button
                                    onClick={async () => {
                                      setOpenMenu(null);

                                      const res =
                                        await GetCandidateInterviewDetails(
                                          candidate.submitedCandidateUID,
                                        );

                                      const data = res?.data || res;

                                      if (
                                        data?.interviewDetails?.status ===
                                        "Success"
                                      ) {
                                        setCandidateDetails({
                                          candidateDetails:
                                            data.interviewDetails
                                              ?.candidateDetails,
                                          jobDetails:
                                            data.interviewDetails?.jobDetails,
                                        });

                                        setInterviewDetails(
                                          data.interviewDetails
                                            ?.jobInterviewers || [],
                                        );

                                        setSelectedCandidate(
                                          data.interviewDetails
                                            ?.candidateDetails,
                                        );
                                        setModalType("BGV");
                                        setShowDetailsModal(true);
                                      }
                                    }}
                                    className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                  >
                                    Start Background Verification
                                  </button>

                                  <button
                                    onClick={async () => {
                                      setOpenMenu(null);

                                      const res =
                                        await GetCandidateInterviewDetails(
                                          candidate.submitedCandidateUID,
                                        );

                                      const data = res?.data || res;

                                      if (
                                        data?.interviewDetails?.status ===
                                        "Success"
                                      ) {
                                        setCandidateDetails({
                                          candidateDetails:
                                            data.interviewDetails
                                              ?.candidateDetails,
                                          jobDetails:
                                            data.interviewDetails?.jobDetails,
                                        });

                                        setInterviewDetails(
                                          data.interviewDetails
                                            ?.jobInterviewers || [],
                                        );

                                        setSelectedCandidate(
                                          data.interviewDetails
                                            ?.candidateDetails,
                                        );
                                        setModalType("Backout");
                                        setShowDetailsModal(true);
                                      }
                                    }}
                                    className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                  >
                                    Back-out
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="py-6 text-red-500 text-center">
                        No Candidate Found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              totalItems={filteredCandidates.length}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              setItemsPerPage={setItemsPerPage}
            />
          </div>
        </div>
      </div>

      {/* MODALS */}
      <CandidateDetailsModal
        show={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setModalType(null);
        }}
        candidateDetails={candidateDetails}
        selectedCandidate={selectedCandidate}
        showInterviewDetailsSection={true}
        interviewDetails={interviewDetails}
        modalActionType={modalType}
        remark={remark}
        setRemark={setRemark}
        onActionSubmit={handleActionSubmit}
        actionLoading={actionLoading}
        showResumeButton={true}
        showSystemRemarkButton={true}
        showSystemRemark={showSystemRemark}
        systemRemarkData={systemRemarkData}
        onViewSystemRemark={handleViewSystemRemark}
        onViewResume={() => handleResumeClick(selectedCandidate)}
        onScheduleClick={handleScheduleClick}
        onViewFeedback={handleViewFeedback}
        systemRemarkLoading={systemRemarkLoading}
      />

      <SystemRemarkModal
        show={showSystemRemarkModal}
        onClose={() => setShowSystemRemarkModal(false)}
        systemRemarkData={systemRemarkData}
      />

      <ScheduleInterviewModal
        show={showScheduleModal}
        onClose={() => {
          setShowScheduleModal(false);
          setShowDetailsModal(true); // reopen candidate modal if needed
        }}
        selectedInterview={selectedInterview}
        interviewDateTime={interviewDateTime}
        setInterviewDateTime={setInterviewDateTime}
        remark={remark}
        setRemark={setRemark}
        loading={loading}
        onSchedule={handleSchedule}
      />

      <InterviewFeedbackModal
        show={showFeedbackModal}
        onClose={() => {
          setShowFeedbackModal(false);
          setShowDetailsModal(true);
        }}
        feedbackData={feedbackData}
      />

      <ColumnFilterModal
        show={!!activeFilter}
        onHide={() => setActiveFilter(null)}
        title={activeFilter}
        items={filterOptions[activeFilter] || []}
        filteredItems={(filterOptions[activeFilter] || []).filter((item) =>
          item?.toLowerCase().includes(filterSearch.toLowerCase()),
        )}
        selectedItems={filters[activeFilter] || []}
        setSelectedItems={(values) =>
          setFilters((prev) => ({
            ...prev,
            [activeFilter]: values,
          }))
        }
        searchText={filterSearch}
        setSearchText={setFilterSearch}
      />

      <ViewResumeModal
        show={showResumeModal}
        onClose={() => setShowResumeModal(false)}
        resumeUrl={resumeUrl}
      />

      {alert && (
        <NormalAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default InterviewScheduledApplication;
